import * as Sentry from '@sentry/react'
import { apiTraceTargets, scrubSentryEvent } from './observability'

/**
 * Initialize Sentry for the Form application
 * This application is embedded as an iFrame on customer sites for lead capture
 */
export function initSentry() {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.warn('Sentry DSN not configured, skipping initialization')
    return
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: __SENTRY_RELEASE__ || undefined,
    initialScope: { tags: { app: 'form' } },
    integrations: [
      Sentry.browserTracingIntegration(),
    ],

    // Performance Monitoring
    tracesSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '1.0'
    ),

    // Trace API calls to backend
    tracePropagationTargets: apiTraceTargets(import.meta.env.VITE_API_URL),

    // Session Replay is off for the public embeddable form.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // Ignore iFrame-related errors that are common in embedded contexts
    ignoreErrors: [
      'SecurityError',
      'cross-origin frame',
      'Failed to read the \'localStorage\' property',
      'Failed to read the \'sessionStorage\' property',
      'Blocked a frame with origin',
      'Non-Error promise rejection captured',
      // Stale JS chunks after deployment — Vercel SPA rewrite serves HTML
      // for missing hashed assets, and the browser rejects the MIME type.
      // A page refresh resolves it. (Sentry CORTADO-PORTAL-9)
      "is not a valid JavaScript MIME type",
      // Network-level failures from the browser fetch — user offline, tunnel,
      // CORS preflight drop, OS sleep mid-request. Not actionable on our side.
      'Failed to fetch',
      'NetworkError when attempting to fetch resource',
      'Load failed',
      // Google Maps Places API internal module load failure.
      // Triggered by deprecation of AutocompleteService (March 2025).
      // We catch and gracefully degrade in places-autocomplete.tsx.
      'Could not load "places_impl"',
    ],

    beforeSend(event) {
      return scrubSentryEvent(event)
    },
    beforeSendTransaction: scrubSentryEvent,
    beforeBreadcrumb: scrubSentryEvent,
  })
}

interface FormContext {
  formId?: string
  organizationId?: string
  sourceUrl?: string
}

/**
 * Set context information about the embedded form
 * This helps track which form/organization generated errors
 */
export function setFormContext(context: FormContext) {
  if (context.formId) {
    Sentry.setTag('form_id', context.formId)
  }
  if (context.organizationId) {
    Sentry.setTag('organization_id', context.organizationId)
  }
  if (context.sourceUrl) {
    Sentry.setTag('source_url', context.sourceUrl)
  }

  // Also set as user context for better tracking
  Sentry.setContext('form', {
    formId: context.formId,
    organizationId: context.organizationId,
    sourceUrl: context.sourceUrl,
  })
}

/**
 * Track form submission events
 * Adds a breadcrumb that shows up in error reports
 */
export function trackFormSubmission(formId: string, success: boolean) {
  Sentry.addBreadcrumb({
    category: 'form',
    message: `Form submission ${success ? 'succeeded' : 'failed'}`,
    level: success ? 'info' : 'error',
    data: {
      formId,
      success,
      timestamp: new Date().toISOString(),
    },
  })
}

// Re-export Sentry for convenience
export { Sentry }
